import React, { useState } from "react";
// TODO: Implement custom API client
import * as qrCodeService from "@/shared/api/services/qrCodeService";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import {
  ArrowDownToLine,
  ArrowUpFromLine,
  CheckCircle2,
  Clock,
  XCircle,
  TrendingUp,
} from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import MerchantLayout from "@/shared/components/merchant/MerchantLayout";
import StatCard from "@/shared/components/merchant/StatCard";
import TransactionTable from "@/shared/components/merchant/TransactionTable";
import TransactionDetailPanel from "@/shared/components/merchant/TransactionDetailPanel";
import { Button } from "@/shared/components/ui/button";
import { Link } from "react-router-dom";
import { createPageUrl } from "@/shared/utils";
import DateFilter from "@/shared/components/dashboard/DateFilter";
import { getDateRange, filterTransactionsByDateRange } from "@/shared/utils/dateFilters";
import { format, eachDayOfInterval, eachHourOfInterval, differenceInHours } from "date-fns";

export default function MerchantDashboard() {
  const { t } = useLanguage();
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [dateFilter, setDateFilter] = useState("today");
  const [customDateRange, setCustomDateRange] = useState({ from: null, to: null });

  // Fetch QR codes instead of mock transactions as requested by user
  const { data: qrCodes = [], isLoading } = useQuery({
    queryKey: ["merchant-qr-codes"],
    queryFn: () => qrCodeService.getMerchantQRCodes(),
  });

  // Map QR codes to transaction format
  const transactions = React.useMemo(() => {
    return qrCodes.map((qr) => ({
      id: qr.id,
      transaction_id: qr.payment_id,
      merchant_ref: qr.reference,
      type: "deposit", // Treat QR codes as deposits
      status: qr.status === "paid" || qr.status === "completed" ? "success" : qr.status,
      amount: qr.amount,
      provider_display: qr.client_bank_name || "-",
      currency: qr.currency,
      created_date: qr.createdAt,
      processed_at: qr.updatedAt,
      completed_at: qr.updatedAt,
      provider_code: "QR Code",
      payment_method: "PROMPTPAY_QR",
      is_qr_code: true,
    })).sort((a, b) => new Date(b.created_date) - new Date(a.created_date));
  }, [qrCodes]);
  console.log('transactions', transactions)

  // Get date range based on filter
  const dateRange = React.useMemo(() => {
    return getDateRange(dateFilter, customDateRange);
  }, [dateFilter, customDateRange]);

  // Filter transactions by date range
  const filteredTransactions = React.useMemo(() => {
    return filterTransactionsByDateRange(
      transactions,
      dateRange.startDate,
      dateRange.endDate
    );
  }, [transactions, dateRange]);

  // Calculate stats
  const stats = React.useMemo(() => {
    return {
      totalDeposits: filteredTransactions
        .filter((tx) => tx.type === "deposit")
        .reduce((sum, tx) => sum + (tx.amount || 0), 0),
      totalWithdrawals: filteredTransactions
        .filter((tx) => tx.type === "withdrawal")
        .reduce((sum, tx) => sum + (tx.amount || 0), 0),
      successful: filteredTransactions.filter((tx) => tx.status === "success").length,
      pending: filteredTransactions.filter((tx) => tx.status === "pending" || tx.status === "processing").length,
      failed: filteredTransactions.filter((tx) => tx.status === "failed").length,
    };
  }, [filteredTransactions]);

  // Generate chart data based on date range
  const chartData = React.useMemo(() => {
    const { startDate, endDate } = dateRange;
    const hoursDiff = differenceInHours(endDate, startDate);

    // If range is less than 2 days, show hourly data
    if (hoursDiff <= 48) {
      const hours = eachHourOfInterval({ start: startDate, end: endDate });
      return hours.map((hour) => {
        const hourTxs = filteredTransactions.filter(
          (tx) => {
            const txDate = new Date(tx.created_date);
            return txDate >= hour && txDate < new Date(hour.getTime() + 60 * 60 * 1000);
          }
        );
        const deposits = hourTxs
          .filter((tx) => tx.type === "deposit" && tx.status === "success")
          .reduce((sum, tx) => sum + (tx.amount || 0), 0);
        const withdrawals = hourTxs
          .filter((tx) => tx.type === "withdrawal" && tx.status === "success")
          .reduce((sum, tx) => sum + (tx.amount || 0), 0);
        return {
          name: format(hour, "HH:mm"),
          deposits,
          withdrawals,
        };
      });
    } else {
      // Show daily data
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      return days.map((day) => {
        const dayTxs = filteredTransactions.filter(
          (tx) => {
            const txDate = new Date(tx.created_date);
            return txDate >= day && txDate < new Date(day.getTime() + 24 * 60 * 60 * 1000);
          }
        );
        const deposits = dayTxs
          .filter((tx) => tx.type === "deposit" && tx.status === "success")
          .reduce((sum, tx) => sum + (tx.amount || 0), 0);
        const withdrawals = dayTxs
          .filter((tx) => tx.type === "withdrawal" && tx.status === "success")
          .reduce((sum, tx) => sum + (tx.amount || 0), 0);
        return {
          name: format(day, "MMM d"),
          deposits,
          withdrawals,
        };
      });
    }
  }, [filteredTransactions, dateRange]);

  const recentTransactions = filteredTransactions.slice(0, 5);
  console.log("recent transactions", recentTransactions)

  return (
    <MerchantLayout currentPage="MerchantDashboard" merchantName="Royal Gaming" merchantCode="MRC001">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">{t("dashboard")}</h1>
            <p className="text-slate-500">{t("welcomeBackOverview")}</p>
          </div>
          <div className="flex items-center gap-3">
            <DateFilter
              value={dateFilter}
              onChange={setDateFilter}
              customDateRange={customDateRange}
              onCustomDateRangeChange={setCustomDateRange}
            />
            <Link to={createPageUrl("MerchantDeposit")}>
              <Button className="gap-2 bg-blue-600 hover:bg-blue-700">
                <ArrowDownToLine className="h-4 w-4" />
                {t("newDeposit")}
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <StatCard
            title={t("totalDeposits")}
            value={`฿${stats.totalDeposits.toLocaleString()}`}
            icon={ArrowDownToLine}
            iconColor="bg-blue-500"
            delay={0}
          />
          <StatCard
            title={t("totalWithdrawals")}
            value={`฿${stats.totalWithdrawals.toLocaleString()}`}
            icon={ArrowUpFromLine}
            iconColor="bg-emerald-500"
            delay={0.1}
          />
          <StatCard
            title={t("successful")}
            value={stats.successful}
            icon={CheckCircle2}
            iconColor="bg-emerald-500"
            delay={0.2}
          />
          <StatCard
            title={t("pending")}
            value={stats.pending}
            icon={Clock}
            iconColor="bg-amber-500"
            delay={0.3}
          />
          <StatCard
            title={t("failed")}
            value={stats.failed}
            icon={XCircle}
            iconColor="bg-red-500"
            delay={0.4}
          />
        </div>

        {/* Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl p-6 border border-slate-100"
        >
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-lg font-semibold text-slate-900">{t("transactionVolume")}</h3>
              <p className="text-sm text-slate-500">{t("transactionPerformance")}</p>
            </div>
            <div className="flex items-center gap-4 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500" />
                <span className="text-slate-600">{t("deposits")}</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-emerald-500" />
                <span className="text-slate-600">{t("withdrawals")}</span>
              </div>
            </div>
          </div>

          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorDeposits" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorWithdrawals" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#64748b", fontSize: 12 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: "#64748b", fontSize: 12 }}
                  tickFormatter={(value) => `฿${(value / 1000).toFixed(0)}K`}
                />
                <Tooltip
                  contentStyle={{
                    background: "#fff",
                    border: "1px solid #e2e8f0",
                    borderRadius: "12px",
                    boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)",
                  }}
                  formatter={(value) => [`฿${value.toLocaleString()}`, ""]}
                />
                <Area
                  type="monotone"
                  dataKey="deposits"
                  stroke="#3b82f6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorDeposits)"
                />
                <Area
                  type="monotone"
                  dataKey="withdrawals"
                  stroke="#10b981"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorWithdrawals)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </motion.div>

        {/* Recent Transactions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-slate-900">{t("recentTransactions")}</h3>
            <Link to={createPageUrl("MerchantTransactions")}>
              <Button variant="ghost" className="text-blue-600 hover:text-blue-700">
                {t("viewAll")}
              </Button>
            </Link>
          </div>
          <TransactionTable
            transactions={recentTransactions}
            isLoading={isLoading}
            onRowClick={setSelectedTransaction}
          />
        </motion.div>
      </div>

      <TransactionDetailPanel
        transaction={selectedTransaction}
        open={!!selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
      />
    </MerchantLayout>
  );
}